import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ShareSheet from '../../components/common/ShareSheet';
import StarRating from '../../components/common/StarRating';
import { getProductById } from '../../services/productService';
import cartService from '../../services/cartService';
import { addToWishlist, removeFromWishlist, checkWishlist } from '../../services/wishlistService';
import { AuthContext } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import {
  getProductReviews,
  createReview,
  checkCanReview
} from '../../services/reviewService';
import VirtualTryOnModal from '../../components/common/VirtualTryOnModal';
import UserHeader from '../../components/header/UserHeader';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { compareSizes } from '../../data/sizeProfiles';
import {
  ShoppingCart, Heart, Share2, Check, ShieldCheck, Package, Repeat, Sparkles,
  ChevronLeft, ChevronRight, Home, X, ZoomIn, Store, Truck, BadgeCheck
} from 'lucide-react';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { userAuth } = useContext(AuthContext);
  const { showAuthModal } = useAuthModal();
  const { addProduct: addToRecentlyViewed } = useRecentlyViewed();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('specifications');
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [displayedReviewCount, setDisplayedReviewCount] = useState(5);
  const [manualNavTick, setManualNavTick] = useState(0);
  const ctaContainerRef = useRef(null);
  const reviewsSectionRef = useRef(null);
  
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review: '' });
  const [submitingReview, setSubmitingReview] = useState(false);
  const [reviewEligibility, setReviewEligibility] = useState({
    canReview: false,
    hasPurchased: false,
    hasReviewed: false,
    reason: ''
  });
  
  // Display images (switches based on selected color variant)
  const [displayImages, setDisplayImages] = useState([]);

  // Virtual Try-On state
  const [showVirtualTryOn, setShowVirtualTryOn] = useState(false);

  // Enhanced debugging
  const debugLog = (message, data = null, type = 'info') => {
    if (process.env.NODE_ENV === 'development') {
      const colors = {
        info: '#2196F3',
        success: '#4CAF50', 
        warning: '#FF9800',
        error: '#F44336'
      };
      
      console.log(
        `%c[ProductDetail] ${message}`,
        `color: ${colors[type]}; font-weight: bold;`,
        data
      );
    }
  };

  useEffect(() => {
    fetchProductDetails();
    fetchReviews(); // Load reviews for the product
    // Only check wishlist if user is authenticated
    if (userAuth.isAuthenticated && userAuth.token) {
      checkProductWishlist();
      checkCanUserReview();
    }
    
    debugLog('Component mounted', {
      productId,
      userAuthenticated: userAuth.isAuthenticated,
      userName: userAuth.user?.name
    });
  }, [productId, userAuth.isAuthenticated]);

  // When selectedColor changes: switch images + auto-select first available size
  useEffect(() => {
    if (!product || !product.variants || !selectedColor) return;

    // Switch images to variant-specific images for this color
    const colorVariantWithImages = product.variants.find(
      v => v.color === selectedColor && v.images && v.images.length > 0
    );
    if (colorVariantWithImages) {
      setDisplayImages(colorVariantWithImages.images);
    } else {
      setDisplayImages(product.images || []);
    }
    setActiveImage(0);

    // Auto-select first in-stock size if current size is invalid for this color
    const colorVariants = product.variants.filter(v => v.color === selectedColor);
    const currentSizeValid = colorVariants.some(v => v.size === selectedSize);

    if (!currentSizeValid || !selectedSize) {
      const firstInStockSize = colorVariants.find(v => v.quantity > 0);
      if (firstInStockSize) {
        setSelectedSize(firstInStockSize.size);
      } else if (colorVariants.length > 0) {
        setSelectedSize(colorVariants[0].size || '');
      }
    }
  }, [selectedColor]);

  // Show the sticky mobile CTA bar when the inline CTA block scrolls off-screen.
  useEffect(() => {
    if (!ctaContainerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(ctaContainerRef.current);
    return () => observer.disconnect();
  }, [product]);

  // ESC closes the image zoom modal + arrow keys navigate.
  useEffect(() => {
    if (!showImageZoom) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowImageZoom(false);
      else if (e.key === 'ArrowRight' && displayImages.length > 0) {
        setActiveImage((i) => (i + 1) % displayImages.length);
      } else if (e.key === 'ArrowLeft' && displayImages.length > 0) {
        setActiveImage((i) => (i - 1 + displayImages.length) % displayImages.length);
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [showImageZoom, displayImages.length]);

  // Auto-advance the hero gallery every 3s. Pauses while the zoom modal is
  // open, only one image exists, or the user just manually navigated (the
  // manualNavTick dep resets the interval so the next auto-step is a fresh
  // 3s away from the user's last click). The interval only reads
  // displayImages.length (not the array reference) — listing the full
  // displayImages here would re-create the interval on every render that
  // returns a new array even when length is unchanged, which serves nothing.
  useEffect(() => {
    if (!displayImages || displayImages.length <= 1) return;
    if (showImageZoom) return;
    const id = setInterval(() => {
      setActiveImage(i => (i + 1) % displayImages.length);
    }, 3000);
    return () => clearInterval(id);
  }, [displayImages.length, showImageZoom, manualNavTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToReviews = () => {
    reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isAuthenticated = () => {
    return userAuth.isAuthenticated && !!userAuth.token;
  };

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      debugLog('📦 Fetching product details...', { productId });
      
      const response = await getProductById(productId);
      if (response.success) {
        setProduct(response.data);
        addToRecentlyViewed(response.data);

        // Initialize display images
        setDisplayImages(response.data.images || []);

        debugLog('✅ Product details fetched', {
          productName: response.data.name,
          hasVariants: response.data.variants?.length > 0,
          variantCount: response.data.variants?.length || 0
        }, 'success');

        // Set default selected size and color (prefer first in-stock variant)
        if (response.data.variants && response.data.variants.length > 0) {
          const firstInStock = response.data.variants.find(v => v.quantity > 0);
          const defaultVariant = firstInStock || response.data.variants[0];

          setSelectedColor(defaultVariant.color || '');
          setSelectedSize(defaultVariant.size || '');

          // Use variant images if available
          if (defaultVariant.images && defaultVariant.images.length > 0) {
            setDisplayImages(defaultVariant.images);
          }

          debugLog('🎨 Default variants set (stock-aware)', {
            selectedSize: defaultVariant.size || 'none',
            selectedColor: defaultVariant.color || 'none',
            inStock: defaultVariant.quantity > 0
          });
        }
      } else {
        debugLog('❌ Failed to fetch product', { response }, 'error');
        toast.error(response.message || 'Failed to fetch product details');
        navigate('/user/dashboard');
      }
    } catch (error) {
      debugLog('💥 Product fetch error', { error }, 'error');
      console.error('Error fetching product details:', error);
      toast.error('Something went wrong while loading product');
      navigate('/user/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const checkProductWishlist = async () => {
    // Only check if user is authenticated with valid token
    if (!userAuth.isAuthenticated || !userAuth.token) {
      setInWishlist(false);
      return;
    }

    setWishlistLoading(true);
    try {
      const response = await checkWishlist(productId);
      if (response.success) {
        setInWishlist(response.data.isInWishlist);
      } else {
        console.log('Wishlist check failed:', response.message);
        setInWishlist(false);
      }
    } catch (error) {
      console.error('Error checking wishlist status:', error);
      setInWishlist(false);
    } finally {
      setWishlistLoading(false);
    }
  };

  const checkCanUserReview = async () => {
    try {
      const response = await checkCanReview(productId);
      if (response.success) {
        setCanReview(response.data.canReview);
        setReviewEligibility({
          canReview: response.data.canReview,
          hasPurchased: response.data.hasPurchased,
          hasReviewed: response.data.hasReviewed,
          reason: response.data.reason
        });
        debugLog('Review eligibility checked', response.data, 'success');
      }
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      setReviewEligibility({
        canReview: false,
        hasPurchased: false,
        hasReviewed: false,
        reason: 'Unable to check review eligibility'
      });
    }
  };

  const handleAddToCart = async () => {
    debugLog('🛒 ADD TO CART - Starting...', {
      productId,
      selectedSize,
      selectedColor,
      userAuth: {
        isAuthenticated: userAuth.isAuthenticated,
        hasToken: !!userAuth.token,
        userName: userAuth.user?.name
      }
    });

    if (!isAuthenticated()) {
      showAuthModal(() => handleAddToCart());
      return;
    }

    // Validate selections for products with variants
    if (product.variants && product.variants.length > 0) {
      const hasSize = product.variants.some(v => v.size);
      const hasColor = product.variants.some(v => v.color);

      debugLog('🎨 Validating variant selections...', {
        hasSize,
        hasColor,
        selectedSize,
        selectedColor,
        variants: product.variants
      });

      if (hasSize && !selectedSize) {
        toast.warning('Please select a size');
        debugLog('❌ Size validation failed', null, 'warning');
        return;
      }

      if (hasColor && !selectedColor) {
        toast.warning('Please select a color');
        debugLog('❌ Color validation failed', null, 'warning');
        return;
      }

      // Check if selected variant is in stock
      const matchedVariant = product.variants.find(
        v => v.color === selectedColor && v.size === selectedSize
      );
      if (matchedVariant && matchedVariant.quantity === 0) {
        toast.error('This size and color combination is out of stock');
        return;
      }
    }

    setCartLoading(true);

    try {
      debugLog('🚀 Calling cartService.addToCart...', {
        productId,
        quantity: 1,
        options: {
          size: selectedSize,
          color: selectedColor
        }
      });
      
      const response = await cartService.addToCart(productId, 1, {
        size: selectedSize,
        color: selectedColor
      });
      
      debugLog('📊 Cart service response:', response);
      
      if (response.success) {
        debugLog('✅ ADD TO CART SUCCESS', {
          message: response.message,
          cartData: response.data
        }, 'success');
        
        toast.success('Product added to cart');
      } else {
        debugLog('❌ ADD TO CART FAILED', {
          message: response.message,
          requiresAuth: response.requiresAuth,
          details: response.details
        }, 'error');
        
        if (response.requiresAuth) {
          debugLog('🔑 Re-authentication required', null, 'warning');
          showAuthModal(() => handleAddToCart());
        } else {
          toast.error(response.message || 'Failed to add to cart');
        }
      }
    } catch (error) {
      debugLog('💥 ADD TO CART ERROR', {
        error: error,
        message: error.message,
        stack: error.stack
      }, 'error');
      
      console.error('Error adding to cart:', error);
      toast.error('Something went wrong while adding to cart');
    } finally {
      setCartLoading(false);
      debugLog('🏁 ADD TO CART COMPLETED', null, 'info');
    }
  };

  const handleBuyNow = async () => {
    debugLog('💰 BUY NOW - Starting...', {
      productId,
      selectedSize,
      selectedColor
    });

    if (!isAuthenticated()) {
      showAuthModal(() => handleBuyNow());
      return;
    }

    // Validate selections for products with variants
    if (product.variants && product.variants.length > 0) {
      const hasSize = product.variants.some(v => v.size);
      const hasColor = product.variants.some(v => v.color);

      if (hasSize && !selectedSize) {
        toast.warning('Please select a size');
        return;
      }

      if (hasColor && !selectedColor) {
        toast.warning('Please select a color');
        return;
      }

      // Check if selected variant is in stock
      const matchedVariant = product.variants.find(
        v => v.color === selectedColor && v.size === selectedSize
      );
      if (matchedVariant && matchedVariant.quantity === 0) {
        toast.error('This size and color combination is out of stock');
        return;
      }
    }

    setCartLoading(true);

    try {
      debugLog('🚀 Adding to cart for buy now...');
      
      const response = await cartService.addToCart(productId, 1, {
        size: selectedSize,
        color: selectedColor
      });
      
      if (response.success) {
        debugLog('✅ BUY NOW - Product added, navigating to cart', null, 'success');
        navigate('/user/cart');
      } else {
        debugLog('❌ BUY NOW FAILED', response, 'error');
        
        if (response.requiresAuth) {
          showAuthModal(() => handleBuyNow());
        } else {
          toast.error(response.message || 'Failed to add to cart');
        }
      }
    } catch (error) {
      debugLog('💥 BUY NOW ERROR', error, 'error');
      console.error('Error with buy now:', error);
      toast.error('Something went wrong');
    } finally {
      setCartLoading(false);
    }
  };

  const toggleWishlist = async () => {
    if (!isAuthenticated()) {
      showAuthModal();
      return;
    }
    
    setWishlistLoading(true);
    try {
      if (inWishlist) {
        const response = await removeFromWishlist(productId);
        if (response.success) {
          setInWishlist(false);
          toast.success('Removed from wishlist');
        } else {
          toast.error(response.message || 'Failed to remove from wishlist');
        }
      } else {
        const response = await addToWishlist(productId);
        if (response.success) {
          setInWishlist(true);
          toast.success('Added to wishlist');
        } else {
          toast.error(response.message || 'Failed to add to wishlist');
        }
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error('Failed to update wishlist');
    } finally {
      setWishlistLoading(false);
    }
  };

  // Debug function for testing
  const debugCartState = () => {
    debugLog('🔧 MANUAL CART DEBUG', {
      userAuth,
      localStorage: {
        userToken: localStorage.getItem('userToken') ? 'present' : 'missing',
        userData: localStorage.getItem('userData') ? 'present' : 'missing'
      }
    });
    
    // Run cart debug function if available
    if (window.debugCartAuth) {
      window.debugCartAuth();
    }
  };

  const debugVirtualTryOn = () => {
    debugLog('🔧 MANUAL VIRTUAL TRY-ON DEBUG', {
      showVirtualTryOn,
      productId,
      userAuth: {
        isAuthenticated: userAuth.isAuthenticated,
        hasToken: !!userAuth.token,
        userName: userAuth.user?.name
      }
    });

    if (window.debugVirtualTryOn) {
      window.debugVirtualTryOn();
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await getProductReviews(productId);
      if (response.success) {
        setReviews(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch reviews');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Something went wrong while loading reviews');
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      showAuthModal();
      return;
    }

    setSubmitingReview(true);
    try {
      const response = await createReview(productId, reviewForm.rating, reviewForm.review);

      if (response.success) {
        toast.success('Review submitted successfully');
        setShowReviewForm(false);
        setReviewForm({ rating: 5, review: '' });
        fetchReviews(); // Refresh reviews
        checkCanUserReview(); // Update review eligibility
      } else {
        toast.error(response.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Something went wrong while submitting review');
    } finally {
      setSubmitingReview(false);
    }
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setReviewForm(prev => ({
      ...prev,
      rating
    }));
  };

  // Deduplicated unique colors (one swatch per unique color name)
  const uniqueColors = useMemo(() => {
    if (!product || !product.variants || product.variants.length === 0) return [];
    const colorMap = new Map();
    product.variants.forEach(v => {
      if (v.color && !colorMap.has(v.color)) {
        colorMap.set(v.color, { name: v.color, code: v.colorCode || '#000000' });
      }
    });
    return Array.from(colorMap.values());
  }, [product]);

  // Sizes filtered by selected color, with stock info. Sorted canonically
  // (alpha → numeric → age) so the buyer always sees small→large regardless of
  // how the variants ended up ordered in the DB.
  const sizesForSelectedColor = useMemo(() => {
    if (!product || !product.variants || product.variants.length === 0) return [];
    const byCanonical = (a, b) => compareSizes(a.size, b.size);
    if (!selectedColor) {
      return Array.from(new Set(product.variants.map(v => v.size).filter(Boolean)))
        .map(size => ({ size, inStock: product.variants.some(v => v.size === size && v.quantity > 0) }))
        .sort(byCanonical);
    }
    const colorVariants = product.variants.filter(v => v.color === selectedColor);
    const sizeSet = new Set();
    const result = [];
    colorVariants.forEach(v => {
      if (v.size && !sizeSet.has(v.size)) {
        sizeSet.add(v.size);
        result.push({ size: v.size, inStock: v.quantity > 0 });
      }
    });
    return result.sort(byCanonical);
  }, [product, selectedColor]);

  // Find the exact variant for current color+size selection
  const selectedVariant = useMemo(() => {
    if (!product || !product.variants || !selectedColor || !selectedSize) return null;
    return product.variants.find(v => v.color === selectedColor && v.size === selectedSize) || null;
  }, [product, selectedColor, selectedSize]);

  if (loading) {
    return (
      <>
        <UserHeader />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-3 border-gray-200 border-t-orange-500"></div>
            </div>
            <p className="text-sm text-gray-500">Loading product...</p>
          </div>
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <UserHeader />
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-sm text-gray-500 mb-6">This product doesn't exist or has been removed.</p>
            <Link to="/user/home" className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">
              Return to Home
            </Link>
          </div>
        </div>
      </>
    );
  }

  const discount = product.mrp && product.zammerPrice < product.mrp
    ? Math.round(((product.mrp - product.zammerPrice) / product.mrp) * 100)
    : 0;

  return (
    <>
      <UserHeader />
      <div className="min-h-screen bg-white pb-32">

        {/* Breadcrumb (Section A) */}
        <div className="max-w-[480px] mx-auto px-4 pt-3">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-gray-500 overflow-x-auto whitespace-nowrap scrollbar-hide">
            <Link to="/user/dashboard" className="flex items-center hover:text-gray-900 transition-colors">
              <Home className="w-3.5 h-3.5" strokeWidth={1.8} />
            </Link>
            {(() => {
              // Build segments from L1-L4 if available, else fall back to the legacy
              // category (Men/Women/Kids) + subCategory so the trail is never lonely.
              const fromLevels = [
                { label: product.categoryLevel1, path: product.categoryLevel1 ? `/user/browse/${encodeURIComponent(product.categoryLevel1)}` : null },
                { label: product.categoryLevel2, path: product.categoryLevel1 && product.categoryLevel2 ? `/user/browse/${encodeURIComponent(product.categoryLevel1)}/${encodeURIComponent(product.categoryLevel2)}` : null },
                { label: product.categoryLevel3, path: product.categoryLevel1 && product.categoryLevel2 && product.categoryLevel3 ? `/user/browse/${encodeURIComponent(product.categoryLevel1)}/${encodeURIComponent(product.categoryLevel2)}/${encodeURIComponent(product.categoryLevel3)}` : null },
                { label: product.categoryLevel4, path: null }
              ].filter(seg => seg.label);

              const segs = fromLevels.length > 0
                ? fromLevels
                : [
                    product.category ? { label: product.category, path: `/user/browse/${encodeURIComponent(product.category === 'Men' ? 'Men Fashion' : product.category === 'Women' ? 'Women Fashion' : 'Kids Fashion')}` } : null,
                    product.subCategory ? { label: product.subCategory, path: null } : null
                  ].filter(Boolean);

              return segs.map((seg, idx, arr) => (
                <React.Fragment key={`${seg.label}-${idx}`}>
                  <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" strokeWidth={2} />
                  {seg.path && idx < arr.length - 1 ? (
                    <Link to={seg.path} className="hover:text-gray-900 transition-colors">{seg.label}</Link>
                  ) : (
                    <span className={idx === arr.length - 1 ? 'text-gray-900 font-medium' : ''}>{seg.label}</span>
                  )}
                </React.Fragment>
              ));
            })()}
          </nav>
        </div>

        {/* Single-column mobile-frame layout — matches the bottom-nav width.
            No more 2-column desktop split with empty space below the image. */}
        <div className="max-w-[480px] mx-auto px-4 pb-6">

          {/* Image Gallery (Section B) — full-width hero with overlay arrows + 3s auto-advance */}
          <div className="mt-3 mb-3 relative">
            <button
              type="button"
              onClick={() => displayImages && displayImages.length > 0 && setShowImageZoom(true)}
              className="group relative w-full aspect-square bg-neutral-50 rounded-xl overflow-hidden border border-gray-200 block"
              aria-label="Zoom image"
            >
              {/* object-top on every product/model photo across the app, not the
                  default centre: catalogue shots are taller than the frames we
                  crop them into, so a centred crop takes equal slices off the
                  top and bottom and beheads the model. Anchoring to the top
                  spends the whole trim on the hem instead. */}
              {displayImages && displayImages.length > 0 ? (
                <>
                  <img
                    src={displayImages[activeImage]}
                    alt={product.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-4 h-4 text-gray-700" strokeWidth={1.8} />
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingCart className="w-16 h-16 text-gray-300" strokeWidth={1} />
                </div>
              )}
            </button>

            {/* Overlay nav controls — only when 2+ images */}
            {displayImages && displayImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage(i => (i - 1 + displayImages.length) % displayImages.length);
                    setManualNavTick(t => t + 1);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-gray-800 shadow-sm flex items-center justify-center transition-colors backdrop-blur-sm"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage(i => (i + 1) % displayImages.length);
                    setManualNavTick(t => t + 1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-gray-800 shadow-sm flex items-center justify-center transition-colors backdrop-blur-sm"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5" strokeWidth={2} />
                </button>

                {/* Dot indicator centered at bottom */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {displayImages.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        i === activeImage ? 'bg-black w-5' : 'bg-white/80 w-1.5'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* More from this catalogue — sibling products row */}
          {product.siblingProducts && product.siblingProducts.length > 0 && (
            <div className="mt-1 mb-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                More from this catalogue
              </p>
              <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1 -mx-4 px-4">
                {product.siblingProducts.map(sib => (
                  <button
                    key={sib._id}
                    onClick={() => navigate(`/user/product/${sib._id}`)}
                    className="flex-shrink-0 w-24 snap-start text-left group"
                  >
                    <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-neutral-50">
                      <img
                        src={sib.images?.[0] || '/placeholder-product.jpg'}
                        alt={sib.name}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-700 mt-1 truncate leading-tight">{sib.name}</p>
                    <p className="text-[11px] font-semibold text-gray-900 leading-tight">₹{sib.zammerPrice}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product Details (Section C) */}
          <div>
              {/* Brand line + Wishlist */}
              <div className="flex items-start justify-between mb-1">
                <div className="min-w-0">
                  {product.brand ? (
                    <p className="text-sm text-gray-500 mb-1">
                      by <span className="font-medium text-gray-700">{product.brand}</span>
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      {product.category || 'ZAMMER'}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={toggleWishlist}
                    disabled={wishlistLoading}
                    aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    {wishlistLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
                    ) : (
                      <Heart
                        className={`w-5 h-5 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                        strokeWidth={1.8}
                      />
                    )}
                  </button>
                  {/* Change 15 — share sits next to the wishlist heart */}
                  <button
                    onClick={() => setShowShareOptions(true)}
                    aria-label="Share this product"
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <Share2 className="w-5 h-5 text-gray-400" strokeWidth={1.8} />
                  </button>
                </div>
              </div>

              {/* Product Name */}
              <h1 className="text-2xl lg:text-[28px] font-bold text-gray-900 leading-tight mb-3">{product.name}</h1>

              {/* Rating row — always render (social proof anchor) */}
              <button
                type="button"
                onClick={scrollToReviews}
                className="flex items-center gap-2 mb-5 group"
              >
                {product.numReviews > 0 ? (
                  <>
                    <StarRating rating={product.averageRating || 0} />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                      {product.averageRating?.toFixed(1) || '0.0'} ({product.numReviews} {product.numReviews === 1 ? 'review' : 'reviews'})
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 group-hover:text-gray-700 transition-colors">
                    No reviews yet · Be the first to review
                  </span>
                )}
              </button>

              {/* Price Section */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                  <p className="text-3xl font-bold text-gray-900">₹{product.zammerPrice}</p>
                  {product.mrp && product.mrp > product.zammerPrice && (
                    <>
                      <p className="text-lg text-gray-400 line-through">MRP: ₹{product.mrp}</p>
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                        {discount}% OFF
                      </span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500">(Inclusive of all taxes)</p>
              </div>

              {/* Color Selector */}
              {uniqueColors.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-900">
                      COLOR: <span className="font-normal text-gray-600">{selectedColor || 'Select'}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {uniqueColors.map((colorObj) => {
                      const colorHasStock = product.variants.some(
                        v => v.color === colorObj.name && v.quantity > 0
                      );
                      return (
                        <button
                          key={colorObj.name}
                          onClick={() => setSelectedColor(colorObj.name)}
                          className={`relative w-10 h-10 rounded-full border-2 transition-all ${
                            selectedColor === colorObj.name
                              ? 'border-black scale-110 ring-2 ring-offset-2 ring-black'
                              : 'border-gray-200 hover:border-gray-400'
                          } ${!colorHasStock ? 'opacity-40' : ''}`}
                          style={{ backgroundColor: colorObj.code }}
                          title={`${colorObj.name}${!colorHasStock ? ' (Out of Stock)' : ''}`}
                        >
                          {selectedColor === colorObj.name && (
                            <Check className="w-5 h-5 text-white mx-auto drop-shadow" strokeWidth={3} />
                          )}
                          {!colorHasStock && (
                            <span className="absolute inset-0 rounded-full"
                              style={{
                                background: 'linear-gradient(135deg, transparent 45%, #ef4444 45%, #ef4444 55%, transparent 55%)'
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size Selector */}
              {sizesForSelectedColor.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-900">
                      SIZE: <span className="font-normal text-gray-600">{selectedSize || 'Select'}</span>
                    </p>
                    <a href="#" className="text-xs font-medium text-gray-600 hover:text-black underline">
                      SIZE CHART
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sizesForSelectedColor.map(({ size, inStock }) => (
                      <button
                        key={size}
                        onClick={() => inStock && setSelectedSize(size)}
                        disabled={!inStock}
                        className={`px-5 py-2.5 border rounded-lg text-sm font-medium transition-all ${
                          selectedSize === size
                            ? 'bg-black text-white border-black'
                            : inStock
                              ? 'bg-white text-gray-900 border-gray-300 hover:border-black cursor-pointer'
                              : 'bg-gray-100 text-gray-300 border-gray-200 line-through cursor-not-allowed'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock + Delivery info (Section C step 8) */}
              <div className="mb-6 space-y-2">
                {selectedVariant && (
                  <div className={`flex items-center gap-2 text-sm ${
                    selectedVariant.quantity === 0
                      ? 'text-red-600'
                      : selectedVariant.quantity <= 5
                        ? 'text-orange-600'
                        : 'text-green-600'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      selectedVariant.quantity === 0
                        ? 'bg-red-500'
                        : selectedVariant.quantity <= 5
                          ? 'bg-orange-500'
                          : 'bg-green-500'
                    }`} />
                    {selectedVariant.quantity === 0
                      ? 'Out of Stock'
                      : selectedVariant.quantity <= 5
                        ? `Only ${selectedVariant.quantity} left in stock!`
                        : 'In Stock'}
                  </div>
                )}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-full pl-2 pr-3 py-1.5 w-fit">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white">
                    <Truck className="w-3.5 h-3.5" strokeWidth={2.2} />
                  </span>
                  <span className="text-sm font-bold text-orange-700 tracking-tight">1 hour delivery</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-600/80 bg-white/60 px-1.5 py-0.5 rounded-full">Fast</span>
                </div>
              </div>

              {/* CTA Buttons (Section D — side-by-side primaries + highlighted Try & Buy) */}
              <div ref={ctaContainerRef} className="mb-5">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={cartLoading}
                    className="bg-black text-white py-3.5 rounded-xl font-bold tracking-tight hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[15px]"
                  >
                    {cartLoading ? 'Adding...' : 'Add to Bag'}
                  </button>
                  <button
                    onClick={handleBuyNow}
                    disabled={cartLoading}
                    className="bg-white border-2 border-black text-black py-3.5 rounded-xl font-bold tracking-tight hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[15px]"
                  >
                    {cartLoading ? 'Processing...' : 'Buy Now'}
                  </button>
                </div>
                <button
                  onClick={() => {
                    if (!isAuthenticated()) {
                      showAuthModal(() => setShowVirtualTryOn(true));
                      return;
                    }
                    setShowVirtualTryOn(true);
                  }}
                  className="group relative w-full overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white py-3.5 rounded-xl font-bold tracking-tight transition-all flex items-center justify-center gap-2 text-[15px] shadow-lg shadow-purple-500/20"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <Sparkles className="w-4 h-4 relative" strokeWidth={2.2} />
                  <span className="relative">Try & Buy — Virtual Try-On</span>
                </button>
              </div>

              {/* Seller Card (Section E) */}
              {product.seller && (product.seller.shop?.name || product.seller.firstName) && (
                <Link
                  to={`/user/shop/${product.seller._id}`}
                  className="block mb-6 p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    {product.seller.shop?.mainImage ? (
                      <img
                        src={product.seller.shop.mainImage}
                        alt={product.seller.shop?.name || 'Shop'}
                        className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-white shadow-sm"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <span className="text-white font-bold text-base">
                          {(product.seller.shop?.name || product.seller.firstName || 'Z').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 leading-tight">Sold by</p>
                      <p className="font-semibold text-gray-900 text-sm truncate flex items-center gap-1.5">
                        {product.seller.shop?.name || product.seller.firstName}
                        <Store className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.8} />
                      </p>
                      {/* The shop's street address is deliberately not shown
                          here — "View Shop" is where a buyer goes for it. */}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1 text-sm font-medium text-gray-700 group-hover:text-black transition-colors">
                      View Shop
                      <ChevronRight className="w-4 h-4" strokeWidth={2} />
                    </div>
                  </div>
                </Link>
              )}

              {/* Trust Badges — GenZ professional, soft pastel chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8 py-5 border-y border-gray-100">
                {[
                  { Icon: ShieldCheck, label: 'Secure',   sub: 'Payments', tint: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
                  { Icon: Package,     label: 'Genuine',  sub: 'Product',  tint: 'bg-blue-50 text-blue-700 ring-blue-100' },
                  { Icon: Sparkles,    label: 'Try &',    sub: 'Buy',      tint: 'bg-purple-50 text-purple-700 ring-purple-100' },
                  { Icon: Repeat,      label: '24-Hour',  sub: 'Returns',  tint: 'bg-orange-50 text-orange-700 ring-orange-100' }
                ].map(({ Icon, label, sub, tint }) => (
                  <div key={label + sub} className="flex flex-col items-center text-center">
                    <div className={`w-9 h-9 rounded-full ${tint} ring-1 flex items-center justify-center mb-1.5`}>
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <p className="text-[11px] leading-tight text-gray-800 font-semibold tracking-tight">
                      {label}<br />{sub}
                    </p>
                  </div>
                ))}
              </div>

              {/* Specifications / Description Tabs */}
              <div className="mb-8">
                <div className="border-b border-gray-200 mb-5">
                  <div className="flex gap-8">
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('specifications')}
                      className={`pb-3 -mb-px text-sm font-semibold transition-colors ${
                        activeDetailTab === 'specifications'
                          ? 'border-b-2 border-black text-gray-900'
                          : 'border-b-2 border-transparent text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      Specifications
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('description')}
                      className={`pb-3 -mb-px text-sm font-semibold transition-colors ${
                        activeDetailTab === 'description'
                          ? 'border-b-2 border-black text-gray-900'
                          : 'border-b-2 border-transparent text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      Description
                    </button>
                  </div>
                </div>

                {activeDetailTab === 'specifications' ? (
                  <dl className="divide-y divide-gray-100 text-sm">
                    {product.category && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Category</dt>
                        <dd className="text-gray-900 col-span-2 leading-snug">
                          {product.categoryPath || product.category}
                        </dd>
                      </div>
                    )}
                    {product.fabricType && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Fabric</dt>
                        <dd className="text-gray-900 col-span-2">{product.fabricType}</dd>
                      </div>
                    )}
                    {product.material && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Material</dt>
                        <dd className="text-gray-900 col-span-2">{product.material}</dd>
                      </div>
                    )}
                    {/* Lehenga set details, shown only when the seller actually
                        entered them. Unlike Composition below, nothing invents a
                        value here — a blank row is better than a wrong one. */}
                    {product.blouseStitching && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Blouse</dt>
                        <dd className="text-gray-900 col-span-2">{product.blouseStitching}</dd>
                      </div>
                    )}
                    {product.lehengaStitching && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Lehenga</dt>
                        <dd className="text-gray-900 col-span-2">{product.lehengaStitching}</dd>
                      </div>
                    )}
                    {product.dupattaIncluded && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Dupatta</dt>
                        <dd className="text-gray-900 col-span-2">
                          {product.dupattaIncluded === 'Yes' ? 'Included' : 'Not included'}
                        </dd>
                      </div>
                    )}
                    {/* Composition is deliberately not shown. It was never
                        entered by sellers — brandProductSeedController fabricated
                        it as `${fabricType} Blend`, falling back to a hardcoded
                        "Cotton 100%" whenever fabricType was missing. That is how
                        a Silk lehenga ended up advertised as Cotton 100%. The
                        field stays on the model so existing data isn't destroyed,
                        but nothing buyer-facing reads it. */}
                    {product.brand && (
                      <div className="grid grid-cols-3 gap-3 py-3">
                        <dt className="text-gray-500 col-span-1">Brand</dt>
                        <dd className="text-gray-900 col-span-2">{product.brand}</dd>
                      </div>
                    )}
                  </dl>
                ) : (
                  <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {product.description
                      ? product.description
                      : <span className="text-gray-400 italic">No description provided.</span>}
                  </div>
                )}
              </div>
          </div>
        </div>

        {/* Reviews Section (Section G) */}
          <div ref={reviewsSectionRef} className="mt-12 pt-12 border-t border-gray-200">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900">Customer Reviews</h2>
                {reviews.length > 0 ? (
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
                      <span className="text-sm font-bold text-emerald-700">
                        {product.averageRating?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                    <StarRating rating={product.averageRating || 0} />
                    <span className="text-sm text-gray-500">
                      {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">No reviews yet</p>
                )}
              </div>

              {userAuth.isAuthenticated && reviewEligibility.canReview && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="flex-shrink-0 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Write a Review
                </button>
              )}
            </div>

            {/* Eligibility hint when user cannot review */}
            {userAuth.isAuthenticated && !reviewEligibility.canReview && reviewEligibility.reason && (
              <div className="mb-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3">
                <BadgeCheck className="w-5 h-5 text-gray-400 flex-shrink-0" strokeWidth={1.8} />
                <p className="text-sm text-gray-600">
                  {reviewEligibility.hasReviewed
                    ? 'You have already reviewed this product. Thank you!'
                    : 'Only verified buyers can review. Purchase this product to share your experience.'}
                </p>
              </div>
            )}

            {/* Review Form Modal */}
            {showReviewForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold mb-4">Write a Review</h3>
                  <form onSubmit={handleReviewSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => handleRatingChange(star)}
                            className="text-2xl"
                          >
                            {star <= reviewForm.rating ? '⭐' : '☆'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
                      <textarea
                        value={reviewForm.review}
                        onChange={handleReviewChange}
                        placeholder="Share your experience with this product"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                        rows="4"
                        required
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitingReview}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {submitingReview ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Reviews List */}
            {reviewsLoading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading reviews...</p>
              </div>
            ) : reviews.length > 0 ? (
              <>
                <div className="space-y-5">
                  {reviews.slice(0, displayedReviewCount).map((review) => (
                    <div key={review._id} className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-white">
                              {review.user?.name?.[0]?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {review.user?.name || 'Anonymous'}
                              </p>
                              {review.isVerifiedPurchase !== false && (
                                <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                                  <BadgeCheck className="w-3 h-3" strokeWidth={2.5} />
                                  Verified Buyer
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <StarRating rating={review.rating} />
                              <span className="text-xs text-gray-400">
                                {new Date(review.createdAt).toLocaleDateString(undefined, {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{review.review}</p>
                    </div>
                  ))}
                </div>

                {/* Show more pagination (local slice) */}
                {reviews.length > displayedReviewCount && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setDisplayedReviewCount((n) => n + 5)}
                      className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                      Show {Math.min(5, reviews.length - displayedReviewCount)} More Review{Math.min(5, reviews.length - displayedReviewCount) > 1 ? 's' : ''}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      Showing {displayedReviewCount} of {reviews.length}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl bg-gray-50">
                <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm font-medium text-gray-700">No reviews yet</p>
                <p className="text-xs text-gray-500 mt-1">
                  {reviewEligibility.canReview
                    ? 'You can be the first to review this product.'
                    : 'Purchase this product to leave the first review.'}
                </p>
              </div>
            )}
          </div>

        {/* Share sheet — shared with the shop page (Changes 9 & 15) */}
        <ShareSheet
          isOpen={showShareOptions}
          onClose={() => setShowShareOptions(false)}
          url={`${window.location.origin}/product/${product._id}`}
          title={product.name}
          text={`Check out ${product.name} on Zammer!`}
        />

        {/* Image Zoom Modal (Section B) */}
        {showImageZoom && displayImages && displayImages.length > 0 && (
          <div
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowImageZoom(false)}
          >
            <button
              type="button"
              onClick={() => setShowImageZoom(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              aria-label="Close zoom"
            >
              <X className="w-5 h-5" strokeWidth={1.8} />
            </button>

            {displayImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((i) => (i - 1 + displayImages.length) % displayImages.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((i) => (i + 1) % displayImages.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" strokeWidth={1.8} />
                </button>
              </>
            )}

            <img
              src={displayImages[activeImage]}
              alt={product.name}
              className="max-h-[90vh] max-w-[90vw] object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
            />

            {displayImages.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {displayImages.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveImage(idx);
                    }}
                    className={`h-1.5 rounded-full transition-all ${
                      activeImage === idx ? 'bg-white w-6' : 'bg-white/40 w-1.5 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sticky Mobile CTA Bar (Section H) — sits flush above the h-14 bottom nav
            and matches its max-w-[480px] centered mobile-frame width. */}
        {showStickyBar && selectedVariant && (
          <div className="lg:hidden fixed bottom-14 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)] px-3 py-2.5">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleAddToCart}
                disabled={cartLoading || selectedVariant.quantity === 0}
                className="bg-black text-white py-2.5 rounded-lg text-sm font-bold tracking-tight hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Bag
              </button>
              <button
                onClick={handleBuyNow}
                disabled={cartLoading || selectedVariant.quantity === 0}
                className="bg-white border-2 border-black text-black py-2.5 rounded-lg text-sm font-bold tracking-tight hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Buy Now
              </button>
            </div>
          </div>
        )}

        {/* Virtual Try-On Modal */}
        <VirtualTryOnModal
          isOpen={showVirtualTryOn}
          onClose={() => setShowVirtualTryOn(false)}
          product={product}
          onTryOnComplete={(result) => {
            console.log('Virtual try-on completed:', result);
          }}
        />

        {/* Bottom nav provided by UserRouteWrapper */}
      </div>
    </>
  );
};

export default ProductDetailPage;